import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Menu,
  MenuItem,
} from "@material-ui/core";
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  CheckCircle as AcknowledgeIcon,
  TrendingUp as EscalateIcon,
} from "@material-ui/icons";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting_on_customer", label: "Waiting on Customer" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const TicketActions = ({
  currentStatus,
  currentPriority,
  canAcknowledge,
  canAssignAgent,
  canEscalate,
  canChangeStatus,
  canSetPriority,
  onStatusChange,
  onPriorityChange,
  onAssignClick,
  onAcknowledgeClick,
  onEscalateClick,
}) => {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [priorityAnchor, setPriorityAnchor] = useState(null);

  const isOpen = currentStatus === "open";
  const isClosed = currentStatus === "closed";
  const isEscalatable = !isOpen && !isClosed;

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      {/* Acknowledge button - only for open tickets, requires assign+status+priority rights */}
      {isOpen && canAcknowledge && onAcknowledgeClick && (
        <Button
          variant="contained"
          size="small"
          onClick={onAcknowledgeClick}
          sx={{
            textTransform: "none",
            fontSize: 13,
            backgroundColor: "#E65100",
            color: "#fff",
            minWidth: "auto",
            "&:hover": { backgroundColor: "#BF360C" },
          }}
        >
          <AcknowledgeIcon style={{ fontSize: 18 }} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 1 }}>Acknowledge</Box>
        </Button>
      )}

      {/* Assign button - disabled for open tickets */}
      {!isOpen && canAssignAgent && (
        <Button
          variant="outlined"
          size="small"
          onClick={onAssignClick}
          sx={{
            textTransform: "none",
            fontSize: 13,
            borderColor: "#E2E8F0",
            color: "#0F172A",
            minWidth: "auto",
          }}
        >
          <PersonAddIcon style={{ fontSize: 18 }} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 1 }}>Assign</Box>
        </Button>
      )}

      {/* Escalate button - for acknowledged/in_progress/waiting tickets */}
      {isEscalatable && canEscalate && onEscalateClick && (
        <Button
          variant="outlined"
          size="small"
          onClick={onEscalateClick}
          sx={{
            textTransform: "none",
            fontSize: 13,
            borderColor: "#D97706",
            color: "#D97706",
            minWidth: "auto",
            "&:hover": { backgroundColor: "#FFFBEB", borderColor: "#D97706" },
          }}
        >
          <EscalateIcon style={{ fontSize: 18 }} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 1 }}>Escalate</Box>
        </Button>
      )}

      {/* Status button - disabled for open tickets (must acknowledge) */}
      {!isOpen && canChangeStatus && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => setStatusAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontSize: 13,
              borderColor: "#E2E8F0",
              color: "#0F172A",
              minWidth: "auto",
            }}
          >
            <EditIcon style={{ fontSize: 18 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 1 }}>Status</Box>
          </Button>
          <Menu
            anchorEl={statusAnchor}
            open={Boolean(statusAnchor)}
            onClose={() => setStatusAnchor(null)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                selected={opt.value === currentStatus}
                onClick={() => {
                  onStatusChange(opt.value);
                  setStatusAnchor(null);
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Priority button - disabled for open tickets (set during acknowledge) */}
      {!isOpen && canSetPriority && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => setPriorityAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontSize: 13,
              borderColor: "#E2E8F0",
              color: "#0F172A",
              minWidth: "auto",
            }}
          >
            <FlagIcon style={{ fontSize: 18 }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, ml: 1 }}>Priority</Box>
          </Button>
          <Menu
            anchorEl={priorityAnchor}
            open={Boolean(priorityAnchor)}
            onClose={() => setPriorityAnchor(null)}
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                selected={opt.value === currentPriority}
                onClick={() => {
                  onPriorityChange(opt.value);
                  setPriorityAnchor(null);
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

TicketActions.propTypes = {
  currentStatus: PropTypes.string,
  currentPriority: PropTypes.string,
  canAcknowledge: PropTypes.bool,
  canAssignAgent: PropTypes.bool,
  canEscalate: PropTypes.bool,
  canChangeStatus: PropTypes.bool,
  canSetPriority: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  onPriorityChange: PropTypes.func.isRequired,
  onAssignClick: PropTypes.func.isRequired,
  onAcknowledgeClick: PropTypes.func,
  onEscalateClick: PropTypes.func,
};

export default TicketActions;
